import { Controller } from "@hotwired/stimulus"

// The recipients of a teacher's communiqué, on its one page (29/10/2026).
//
//   1. The teacher ticks one or more classes (chips).
//   2. « Tous les parents » / « Parents d'élèves spécifiques » shows once a
//      class is ticked.
//   3. With « Parents d'élèves spécifiques », the pupils of each ticked class
//      are loaded below (one block per class); unticking a class drops its
//      block, choosing « Tous les parents » drops them all.
//
// The pupils already chosen (`data-picked` on the students target) are
// ticked again when their class is loaded.
export default class extends Controller {
  static targets = ["classroom", "mode", "modeSection", "students"]
  static values = {
    studentsUrl: String,
    communicationId: String,
    loadingText: { type: String, default: "Loading…" },
    errorText: { type: String, default: "Loading error" },
  }

  connect() {
    this.picked = new Set(JSON.parse(this.studentsTarget.dataset.picked || "[]").map(String))
    this.sync()
  }

  sync() {
    const ticked = this.classroomTargets.filter((box) => box.checked)
    this.modeSectionTarget.classList.toggle("hidden", ticked.length === 0)
    const wanted = this.specificMode() ? ticked.map((box) => box.value) : []

    this.blocks().forEach((block) => {
      if (!wanted.includes(block.dataset.studentsFor)) {
        this.rememberTicks(block)
        block.remove()
      }
    })
    wanted.forEach((id) => {
      if (!this.block(id)) this.load(id)
    })
  }

  // ---- private ----

  specificMode() {
    return this.modeTargets.some((radio) => radio.checked && radio.value === "specific_parents")
  }

  blocks() {
    return Array.from(this.studentsTarget.querySelectorAll("[data-students-for]"))
  }

  block(id) {
    return this.studentsTarget.querySelector(`[data-students-for="${CSS.escape(id)}"]`)
  }

  rememberTicks(block) {
    block.querySelectorAll("input[type='checkbox']").forEach((box) => {
      if (box.checked) this.picked.add(box.value)
      else this.picked.delete(box.value)
    })
  }

  async load(id) {
    const holder = document.createElement("div")
    holder.dataset.studentsFor = id
    holder.innerHTML = `<p class="ds-field__hint py-2">${this.escapeHTML(this.loadingTextValue)}</p>`
    this.studentsTarget.appendChild(holder)

    const params = new URLSearchParams({ classroom_id: id })
    if (this.communicationIdValue) params.append("communication_id", this.communicationIdValue)

    try {
      const response = await fetch(`${this.studentsUrlValue}?${params}`, {
        headers: { Accept: "text/html", "X-Requested-With": "XMLHttpRequest" },
      })
      if (!holder.isConnected) return
      if (!response.ok) throw new Error(String(response.status))
      holder.outerHTML = await response.text()
      const block = this.block(id)
      block?.querySelectorAll("input[type='checkbox']").forEach((box) => {
        if (this.picked.has(box.value)) box.checked = true
      })
    } catch {
      if (holder.isConnected) {
        holder.innerHTML = `<p class="ds-field__error py-2">${this.escapeHTML(this.errorTextValue)}</p>`
      }
    }
  }

  escapeHTML(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
